import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Col, Button, Row, Container } from "react-bootstrap";
import { CustomModal, FiltersButton, TableHeader, WithRoleAccess } from "@services/ui-components";
import VioletBadge from "@services/ui-components/src/VioletBadge/VioletBadge";
import { useReactiveVar } from "@apollo/client";
import AddNewProductListTable from "./AddNewProductListTable/AddNewProductListTable";
import { ProductsFilterI } from "../Products/types";
import ProductsFilterWrapper from "../Products/ProductsList/ProductsFilterWrapper";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { CompanyAccess, ProductProxy } from "@me-team/host/main/graphql/types";
import { useGetProductForAddStorageLazyQuery } from "@me-team/host/main/addNewProductList/graphql/addNewProductList.hooks";
import { currentCompanyIdVar } from "@me-team/host/main/globalVar/state";
import { DeepPartial } from "@apollo/client/utilities";

const AddNewProductList: React.FC = () => {
    const { t } = useTranslation();
    const { id, branchId } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const [showModal, setShowModal] = useState(false);
    const [name, setName] = useState();
    const [showFilters, setShowFilters] = useState(false);
    const [existingProduct, setExistingProduct] = useState(null);
    const currentCompanyId = useReactiveVar(currentCompanyIdVar);
    const initialValueFilter: ProductsFilterI = {
        nameSkuBarcode: null,
        categoryId: null,
        brand: null,
    };
    const [showDuplicateProductModal, setShowDuplicateProductModal] = useState<boolean>(false);
    const [productForChange, setProductForChange] = useState<ProductProxy>(null);
    const [filters, setFilters] = useState(initialValueFilter);
    const handleAddWarehouseModal = (product?: ProductProxy) => {
        if (product) {
            setExistingProduct(product);
        }
        setShowModal((prevState) => !prevState);
    };

    const [getProductForAddStorage] = useGetProductForAddStorageLazyQuery({
        variables: { branchId: Number(branchId), companyId: Number(currentCompanyId), needsFilterByBranch: false },
        context: {
            errorType: "local",
        },
        onError: (error) => {
            console.error(error);
        },
    });

    const handleOpenDuplicateProductModal = async (productId: number) => {
        const { data } = await getProductForAddStorage({
            variables: { branchId: Number(branchId), productId: productId, companyId: Number(currentCompanyId) },
        });
        const product: DeepPartial<ProductProxy> = data?.products?.items[0];
        product && setExistingProduct(product);
        setShowDuplicateProductModal(true);
    };

    const handleNavigateToAddProduct = () => {
        existingProduct && handleAddWarehouseModal(existingProduct);
        setShowDuplicateProductModal(false);
    };
    const handleCloseDuplicateProductModal = () => {
        setProductForChange(existingProduct);
        setExistingProduct(null);
        setShowDuplicateProductModal(false);
        handleAddWarehouseModal();
    };

    const toggleFilters = () => setShowFilters(!showFilters);
    return (
        <>
            <VioletBadge text={t('Click “+” to add a product or “+ Create new product" to create and add.')} />
            <TableHeader
                title={t(`${t("Add products to the warehouse")} “${name}”`)}
                paths={[
                    { path: location?.pathname, label: t("Warehouse accounting") },
                    { path: `/warehouses/branch/${branchId}`, label: t("Warehouses") },
                    { path: `/warehouses/warehouses-goods/branch/${branchId}/${id}`, label: name },
                    { path: location?.pathname, label: t("Add products to the warehouse") },
                ]}
                colSize={6}
                btnColSize={6}
                buttons={
                    <Row className="g-3 justify-content-end align-items-center mt-4 mt-lg-0">
                        <Col md lg={"auto"} xl={"auto"}>
                            <Button
                                variant="outline-primary"
                                className="w-100"
                                onClick={() => navigate(`/warehouses/warehouses-goods/branch/${branchId}/${id}`)}
                            >
                                {t("Back to warehouse products")}
                            </Button>
                        </Col>
                        <WithRoleAccess permission={CompanyAccess.WarehouseProductCreateAndAdd}>
                            <Col xs={12} sm={6} md="auto">
                                <Button
                                    variant="primary"
                                    className="w-100 text-white text-truncate"
                                    onClick={() => handleAddWarehouseModal()}
                                >
                                    <i className="bi bi-plus-lg fw-bold me-1 w-100 h-100"></i>
                                    {t("Create Product")}
                                </Button>
                            </Col>
                        </WithRoleAccess>
                        <Col xs={12} sm={6} md="auto" lg={4} xl={2} xxl="auto">
                            <FiltersButton
                                filter={filters}
                                showFilters={showFilters}
                                onClick={toggleFilters}
                                classesText="d-xl-none d-xxl-inline-block"
                            />
                        </Col>
                    </Row>
                }
                showFilters={showFilters}
            >
                <ProductsFilterWrapper setFilters={setFilters} stylesAddNewProductList />
            </TableHeader>
            <AddNewProductListTable
                filters={filters}
                handleModal={handleAddWarehouseModal}
                setName={setName}
                showModal={showModal}
                onHide={() => ""}
                existingProduct={existingProduct}
                setExistingProduct={() => setExistingProduct(null)}
                initialValueFilter={initialValueFilter}
                onDuplicateProductModal={handleOpenDuplicateProductModal}
                setProductForChange={setProductForChange}
                productForChange={productForChange}
            />
            <CustomModal
                show={showDuplicateProductModal}
                handleClose={handleCloseDuplicateProductModal}
                title={
                    <Container>
                        <Row>
                            <h5 className="m-0 p-0 fw-normal">{t("The product already exists")}</h5>
                        </Row>
                    </Container>
                }
                body={
                    <Container>
                        <Row>
                            <Col>
                                <span>
                                    {t(
                                        "This product is already in the system, to add it to the warehouse, click “OK”."
                                    )}
                                </span>
                            </Col>
                        </Row>
                    </Container>
                }
                buttons={[
                    {
                        text: t("Ok"),
                        onClick: handleNavigateToAddProduct,
                        variant: "primary",
                        className: "w-100 fw-normal",
                    },
                ]}
            />
        </>
    );
};

export default AddNewProductList;
